import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, Users, Trophy, Clock, CheckCircle, 
  AlertCircle, TrendingUp, UserPlus, ChevronRight,
  Star, Zap, Target, Activity, Eye, X, MessageSquare,
  Award, FileText, Wifi, WifiOff, ThumbsUp, Send
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';
import { toast } from 'sonner';

const SuperAdminMonitoring = () => {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [evaluationDetails, setEvaluationDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [stats, setStats] = useState({
    total_assessments: 0,
    in_progress: 0,
    completed: 0,
    pending_evaluation: 0,
    pending_approval: 0,
    luminary: 0,
    inspired: 0,
    aspiring: 0
  });
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchMonitoringData();
  }, []);

  const fetchMonitoringData = async () => {
    try {
      setLoading(true);
      
      // Fetch assessment phases with evaluation details
      const phasesResponse = await fetch(`${BACKEND_URL}/api/admin/assessment-monitoring`);
      const phasesData = await phasesResponse.json();
      setPhases(Array.isArray(phasesData) ? phasesData : []);

      // Fetch organizations for certification counts
      const orgsResponse = await fetch(`${BACKEND_URL}/api/admin/organizations`);
      const orgsData = await orgsResponse.json();
      const orgsList = Array.isArray(orgsData) ? orgsData : [];

      // Calculate stats
      const phasesArray = Array.isArray(phasesData) ? phasesData : [];
      const calculatedStats = {
        total_assessments: phasesArray.length,
        in_progress: phasesArray.filter(p => p.overall_status === 'in_progress').length,
        completed: phasesArray.filter(p => p.overall_status === 'completed' || p.results_published).length,
        pending_evaluation: phasesArray.filter(p => p.evaluation_status === 'in_progress').length,
        pending_approval: phasesArray.filter(p => p.evaluation_status === 'completed' && !p.results_published).length,
        luminary: orgsList.filter(o => o.certification_level === 'Luminary').length,
        inspired: orgsList.filter(o => o.certification_level === 'Inspired').length,
        aspiring: orgsList.filter(o => o.certification_level === 'Aspiring').length
      };
      setStats(calculatedStats);
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluationDetails = async (organizationId, evaluatorId) => {
    setDetailsLoading(true);
    try {
      // Fetch evaluator scores and remarks
      const response = await fetch(`${BACKEND_URL}/api/evaluator-scores/${organizationId}/${evaluatorId || 'all'}`);
      if (response.ok) {
        const data = await response.json();
        setEvaluationDetails(data);
      }
    } catch (error) {
      console.error('Error fetching evaluation details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewDetails = async (phase) => {
    setSelectedAssessment(phase);
    if (phase.evaluator_id) {
      await fetchEvaluationDetails(phase.organization_id, phase.evaluator_id);
    }
  };

  const handleApproveAndPublish = async () => {
    if (!selectedAssessment) return;
    
    setApproving(true);
    try {
      // Run CIS engine and approve
      const response = await fetch(`${BACKEND_URL}/api/admin/approve-certification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: selectedAssessment.organization_id,
          assessment_id: selectedAssessment.assessment_id
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Certification approved! Level: ${result.certification_level}`);
        setShowApprovalModal(false);
        setSelectedAssessment(null);
        fetchMonitoringData();
      } else {
        toast.error('Error approving certification');
      }
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Error approving certification');
    } finally {
      setApproving(false);
    }
  };

  const getStatusBadge = (phase) => {
    // Determine detailed status
    if (phase.results_published) {
      return { label: 'Published', color: 'bg-green-100 text-green-800 border-green-300' };
    }
    if (phase.evaluation_status === 'completed') {
      return { label: 'Pending Approval', color: 'bg-purple-100 text-purple-800 border-purple-300' };
    }
    if (phase.evaluation_status === 'in_progress') {
      return { label: 'Evaluation In Progress', color: 'bg-orange-100 text-orange-800 border-orange-300' };
    }
    if (phase.quantitative_status === 'completed') {
      return { label: 'Quantitative Done', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' };
    }
    if (phase.quantitative_status === 'in_progress') {
      return { label: 'Quantitative In Progress', color: 'bg-blue-100 text-blue-800 border-blue-300' };
    }
    if (phase.payment_status === 'completed') {
      return { label: 'Payment Done', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
    }
    if (phase.qualitative_status === 'completed') {
      return { label: 'Qualitative Done', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' };
    }
    if (phase.qualitative_status === 'in_progress') {
      return { label: 'Qualitative In Progress', color: 'bg-sky-100 text-sky-800 border-sky-300' };
    }
    return { label: 'Not Started', color: 'bg-gray-100 text-gray-800 border-gray-300' };
  };

  const getTierBadge = (tier) => {
    const badges = {
      Luminary: { icon: Star, color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-500' },
      Inspired: { icon: Zap, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-400' },
      Aspiring: { icon: Target, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-500' }
    };
    return badges[tier] || badges.Aspiring;
  };

  if (loading) {
    return (
      <SystemAdminLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading monitoring data...</p>
          </div>
        </div>
      </SystemAdminLayout>
    );
  }

  return (
    <SystemAdminLayout>
      <div className="bg-gray-50 min-h-full">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-8 px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-black mb-2">Assessment Monitoring</h1>
                <p className="text-blue-100">Track all assessments, evaluations, and certifications</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600">Total</p>
                  <p className="text-2xl font-black text-gray-900">{stats.total_assessments}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600">In Progress</p>
                  <p className="text-2xl font-black text-yellow-600">{stats.in_progress}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600">Evaluating</p>
                  <p className="text-2xl font-black text-orange-600">{stats.pending_evaluation}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ThumbsUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600">Pending Approval</p>
                  <p className="text-2xl font-black text-purple-600">{stats.pending_approval}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600">Completed</p>
                  <p className="text-2xl font-black text-green-600">{stats.completed}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Certification Distribution */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-500 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <Star className="w-8 h-8 text-yellow-600" />
                <h3 className="text-xl font-black text-yellow-900">Luminary</h3>
              </div>
              <p className="text-4xl font-black text-yellow-900">{stats.luminary}</p>
              <p className="text-sm text-yellow-700">85-100 points</p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-400 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <Zap className="w-8 h-8 text-gray-600" />
                <h3 className="text-xl font-black text-gray-900">Inspired</h3>
              </div>
              <p className="text-4xl font-black text-gray-900">{stats.inspired}</p>
              <p className="text-sm text-gray-700">65-84 points</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-500 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <Target className="w-8 h-8 text-orange-600" />
                <h3 className="text-xl font-black text-orange-900">Aspiring</h3>
              </div>
              <p className="text-4xl font-black text-orange-900">{stats.aspiring}</p>
              <p className="text-sm text-orange-700">40-64 points</p>
            </div>
          </div>

          {/* Assessment List */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-8 py-6">
              <h2 className="text-2xl font-black">All Assessments</h2>
              <p className="text-blue-100">Click on any assessment to view evaluator progress and remarks</p>
            </div>

            <div className="p-6">
              {phases.length === 0 ? (
                <div className="text-center py-12">
                  <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Assessments</h3>
                  <p className="text-gray-600">No assessment phases have been initiated yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {phases.map((phase) => {
                    const statusBadge = getStatusBadge(phase);
                    const tierBadge = phase.certification_tier ? getTierBadge(phase.certification_tier) : null;
                    const TierIcon = tierBadge?.icon;
                    
                    return (
                      <div
                        key={`${phase.assessment_id}-${phase.organization_id}`}
                        className="border-2 border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer"
                        onClick={() => handleViewDetails(phase)}
                        data-testid={`assessment-${phase.organization_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-gray-900">
                                {phase.organization_name || phase.organization_id}
                              </h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-bold border ${statusBadge.color}`}>
                                {statusBadge.label}
                              </span>
                              {phase.certification_tier && tierBadge && (
                                <div className={`flex items-center gap-1 px-3 py-1 rounded-full border-2 ${tierBadge.bg} ${tierBadge.border}`}>
                                  <TierIcon className={`w-4 h-4 ${tierBadge.color}`} />
                                  <span className={`text-xs font-bold ${tierBadge.color}`}>
                                    {phase.certification_tier}
                                  </span>
                                </div>
                              )}
                              {phase.evaluator_online && (
                                <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                  <Wifi className="w-3 h-3" />
                                  Evaluator Online
                                </span>
                              )}
                            </div>

                            {/* Progress Bar */}
                            <div className="grid grid-cols-5 gap-2 text-xs mt-3">
                              <div className={`text-center py-2 rounded ${phase.qualitative_status === 'completed' ? 'bg-green-100 text-green-700' : phase.qualitative_status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                Qualitative {phase.qualitative_progress || 0}%
                              </div>
                              <div className={`text-center py-2 rounded ${phase.payment_status === 'completed' ? 'bg-green-100 text-green-700' : phase.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'}`}>
                                Payment
                              </div>
                              <div className={`text-center py-2 rounded ${phase.quantitative_status === 'completed' ? 'bg-green-100 text-green-700' : phase.quantitative_status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                Quantitative {phase.quantitative_progress || 0}%
                              </div>
                              <div className={`text-center py-2 rounded ${phase.evaluation_status === 'completed' ? 'bg-green-100 text-green-700' : phase.evaluation_status === 'in_progress' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>
                                Evaluation
                              </div>
                              <div className={`text-center py-2 rounded ${phase.results_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                Results
                              </div>
                            </div>

                            {/* Evaluator Info */}
                            {phase.evaluator_name && (
                              <p className="text-sm text-gray-600 mt-2">
                                Evaluator: <span className="font-semibold">{phase.evaluator_name}</span>
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-4 ml-4">
                            {/* Score Display */}
                            {phase.overall_score !== undefined && phase.overall_score !== null && (
                              <div className="text-center px-4 py-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                                <p className="text-xs text-blue-600 font-semibold">Score</p>
                                <p className="text-2xl font-black text-blue-900">{phase.overall_score}</p>
                              </div>
                            )}

                            {/* View Details */}
                            <button
                              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200"
                              onClick={(e) => { e.stopPropagation(); handleViewDetails(phase); }}
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>

                            {/* Approve Button */}
                            {phase.evaluation_status === 'completed' && !phase.results_published && (
                              <button
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setSelectedAssessment(phase);
                                  setShowApprovalModal(true);
                                }}
                                data-testid="approve-btn"
                              >
                                <ThumbsUp className="w-4 h-4" />
                                Review & Approve
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Evaluation Details Modal */}
      {selectedAssessment && !showApprovalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-black">{selectedAssessment.organization_name}</h2>
                <p className="text-indigo-200">Evaluation Progress & Remarks</p>
              </div>
              <button onClick={() => { setSelectedAssessment(null); setEvaluationDetails(null); }} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* Evaluator Info */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{selectedAssessment.evaluator_name || 'No Evaluator Assigned'}</p>
                      <p className="text-sm text-gray-600">Assigned Evaluator</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedAssessment.evaluator_online ? (
                      <span className="flex items-center gap-1 text-green-600 bg-green-100 px-3 py-1 rounded-full text-sm font-semibold">
                        <Wifi className="w-4 h-4" />
                        Online
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-500 bg-gray-100 px-3 py-1 rounded-full text-sm">
                        <WifiOff className="w-4 h-4" />
                        Offline
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-black text-blue-900">{selectedAssessment.evaluation_progress?.scored || 0}</p>
                  <p className="text-sm text-blue-600">Questions Scored</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-black text-purple-900">{selectedAssessment.evaluation_progress?.total || 0}</p>
                  <p className="text-sm text-purple-600">Total Questions</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-black text-green-900">{selectedAssessment.overall_score || '-'}</p>
                  <p className="text-sm text-green-600">Current Score</p>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-black text-orange-900">{selectedAssessment.certification_tier || '-'}</p>
                  <p className="text-sm text-orange-600">Projected Tier</p>
                </div>
              </div>

              {/* Evaluator Remarks */}
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-600" />
                Evaluator Remarks
              </h3>

              {detailsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading remarks...</p>
                </div>
              ) : evaluationDetails?.scores?.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {evaluationDetails.scores.map((score, idx) => (
                    <div key={idx} className="bg-white border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">{score.pillar_name || `Pillar ${score.pillar_number}`}</p>
                          <p className="text-xs text-gray-500 truncate">{score.question_text?.substring(0, 80)}...</p>
                        </div>
                        <div className="text-right ml-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                            score.score >= 4 ? 'bg-green-100 text-green-700' :
                            score.score >= 2 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {score.score}/5 ({score.points} pts)
                          </span>
                        </div>
                      </div>
                      {score.evaluator_remarks && (
                        <div className="bg-amber-50 rounded-lg p-3 mt-2">
                          <p className="text-sm text-amber-900">{score.evaluator_remarks}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No evaluation remarks yet</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => { setSelectedAssessment(null); setEvaluationDetails(null); }}
                className="px-6 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-semibold"
              >
                Close
              </button>
              {selectedAssessment.evaluation_status === 'completed' && !selectedAssessment.results_published && (
                <button
                  onClick={() => setShowApprovalModal(true)}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center gap-2"
                >
                  <ThumbsUp className="w-5 h-5" />
                  Approve & Publish
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approval Confirmation Modal */}
      {showApprovalModal && selectedAssessment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Approve Certification</h2>
                  <p className="text-sm text-gray-600">{selectedAssessment.organization_name}</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-amber-800 text-sm">
                  <strong>Important:</strong> Approving this certification will:
                </p>
                <ul className="list-disc list-inside text-amber-700 text-sm mt-2 space-y-1">
                  <li>Run the CIS Scoring Engine to calculate final score</li>
                  <li>Determine the certification level (Luminary/Inspired/Aspiring)</li>
                  <li>Publish the results to the organization's dashboard</li>
                  <li>Send notification to the organization</li>
                </ul>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setShowApprovalModal(false); }}
                  className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-semibold"
                  disabled={approving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleApproveAndPublish}
                  disabled={approving}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                  data-testid="confirm-approve-btn"
                >
                  {approving ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  Confirm & Publish
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SystemAdminLayout>
  );
};

export default SuperAdminMonitoring;
